/**
 * OrderBookCostMix — what the current node's cost is MADE of, as a ring: material ·
 * subcontract · machine · labour · other. The bars already show this per child; this shows
 * it for the node you are standing on, and it re-reads every time you descend.
 *
 * Same five colours as the bars (the validated 5-slot palette), so the ring and the stack
 * are one vocabulary.
 *
 * ⚠ THE PANEL DOES NOT DECIDE WHAT IT IS DRAWING. It draws the composition the server
 * attached to a cost, and the CALLER says where that cost came from (`source`) and what to
 * say when there is none (`refusal`). The two facts answer both differently: the order book
 * names the basis the reader picked, and margin names what its one cost is made of.
 *
 * ⭐ AND ON MARGIN IT NOW DRAWS AT GROUP (24 Aug 2026). Every row decomposes whichever basis
 * priced it, so over one cost column the five slots tie at every node. Under the retired
 * two-column split a Group node's components tied to neither column and the ring refused.
 */

import React from 'react'
import DonutChart from './kit/charts/DonutChart'
import { fillsFor } from './kit/charts/marginBreakdownScale'
import { useTheme } from '../contexts/ThemeContext'
import { formatCurrency } from '../utils/formatters'
import { type CostComponent } from '../viewmodels/orderBook'
import type { OrderBookBasisTotals, OrderBookComposition } from '../types/orderBook'

const CARD = 'rounded-xl border border-line bg-panel'
const HEAD = 'px-4 py-2.5 border-b border-line2 text-[13px] font-semibold text-ink'

/** The composition keyed the way the palette and the bars order it. */
const slots = (c: OrderBookComposition): { name: CostComponent; value: number }[] => [
  { name: 'Material', value: c.material ?? 0 },
  { name: 'Subcontract', value: c.subcontract ?? 0 },
  { name: 'Machine', value: c.machine ?? 0 },
  { name: 'Labour', value: c.labour ?? 0 },
  { name: 'Other', value: c.other ?? 0 },
]

function Refusal({ reason }: { reason: string }): React.JSX.Element {
  return (
    <div className={CARD}>
      <div className={HEAD}>Cost mix</div>
      <p className="px-4 py-6 text-center text-[11.5px] text-mut2">{reason}</p>
    </div>
  )
}

/** Each slot with its £ and its share of the node's cost — the ring, read as numbers. */
function Breakdown({ parts, fills, cost }: {
  parts: { name: CostComponent; value: number }[]
  fills: string[]
  cost: number
}): React.JSX.Element {
  return (
    <ul className="px-4 pb-3 pt-1">
      {parts.map((part, i) => (
        <li key={part.name} className="flex items-center gap-2 py-0.5 font-mono text-[10.5px]">
          <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: fills[i] }} />
          <span className="text-mut">{part.name}</span>
          <span className="ml-auto tabular-nums text-ink">{formatCurrency(part.value, '£', 0)}</span>
          <span className="w-11 shrink-0 text-right tabular-nums text-mut2">
            {((100 * part.value) / cost).toFixed(1)}%
          </span>
        </li>
      ))}
    </ul>
  )
}

export default function OrderBookCostMix({ totals, source, refusal, footnote }: {
  totals: OrderBookBasisTotals
  /** Where these numbers come from, as a chip beside the title. Not decoration: an Oracle
   * mix is a QUOTE, priced before the work, and must never read as an outcome. */
  source: string
  /** What to say when there is no composition to draw. */
  refusal: string
  /** An extra caveat under the ring, where one slot is modelled rather than recorded. */
  footnote?: React.ReactNode
}): React.JSX.Element {
  const { theme } = useTheme()
  const { composition, cost } = totals
  if (composition.material === null || cost === null || cost <= 0) {
    return <Refusal reason={refusal} />
  }
  const fills = fillsFor(theme === 'light' ? 'light' : 'dark')
  const parts = slots(composition)
  const segments = parts.map((p, i) => ({ label: p.name, value: p.value, color: fills[i] }))
  return (
    <div className={CARD}>
      <div className={HEAD}>
        Cost mix
        <span className="ml-2 font-mono text-[10px] font-normal text-mut2">{source}</span>
      </div>
      <div className="flex flex-col items-center gap-1 px-4 pt-3">
        <DonutChart
          segments={segments}
          centerLabel={formatCurrency(cost, '£', 0)}
          centerSub="TOTAL COST"
        />
      </div>
      <Breakdown parts={parts} fills={fills} cost={cost} />
      {footnote && <div className="px-4 pb-3 text-[10.5px] leading-snug text-mut2">{footnote}</div>}
    </div>
  )
}
